import { create } from 'zustand'
import baseStore from '@/store/baseStore.ts'
import {
  getTodoCount,
  getTodoItem,
  getTodoList,
  getFollowAppList,
  getFollowList,
} from '@/services/workorder.ts'
import microApp, { getActiveApps } from '@micro-zoe/micro-app'
import { navigatePage } from '@/common/utils.ts'
import { uniqueId } from 'lodash-es'
import workOrder from '@/model/WorkOrder.ts'
import { workOrderPath } from '@/utils/workOrderPath.ts'

export const WORK_ORDER_TABS = ['TODO', 'COMPLETED', 'STARTER', 'CANCELLED']
export const WORK_ORDER_ENUMS = {
  todo: WORK_ORDER_TABS[0],
  completed: WORK_ORDER_TABS[1],
  starter: WORK_ORDER_TABS[2],
  canceled: WORK_ORDER_TABS[4],
}

const createState = () => ({
  isInit: false,
  workOrderPath: '/flow',
  workOrderCollect: [],
  workOrderData: [],
  workOrderCount: {},
  workOrderCache: {},
  workOrderMidState: {},
  workOrderExpandKeys: [WORK_ORDER_TABS[0]],
  selectedTabKey: null,
  selectedAppCode: null,
  selectedItemId: null,
  noMoreDict: {},
  loadingDict: {}, // 0 未加载 1 上拉加载中 2 下拉加载中 3 刷新
  workOrderCollectTab: WORK_ORDER_TABS,
  keyword: '',
})

let _selectedItemKey = ''
const PAGE_SIZE = 10
let pollTimer = null as any
let pollCount = 0

const useWorkOrderStore = create((set, get: any) => ({
  ...baseStore({ createState, set, get }),
  setWorkOrderExpandKeys: workOrderExpandKeys => get().setData({ workOrderExpandKeys }),
  fetchWorkOrderCollect: async () => {
    getTodoCount().then(workOrderCount => {
      get().setData({ workOrderCount })
    })

    Promise.all([
      getTodoList({ tab: WORK_ORDER_TABS[0] }),
      getTodoList({ tab: WORK_ORDER_TABS[1] }),
      getTodoList({ tab: WORK_ORDER_TABS[2] }),
      getFollowAppList(),
      getTodoList({ tab: WORK_ORDER_TABS[3] }),
    ]).then((res: any) => {
      const convert = data =>
        data.map((item: any) => ({
          ...item,
          key: item.appCode,
          name: item.appName,
          icon: item.appIcon,
        }))
      // getFollowList({appCode:''}).then((res1:any)=>{
      //   console.log('-----------',res1)
      // })
      const workOrderCollect = [
        {
          key: WORK_ORDER_TABS[0],
          countKey: 'todoCount',
          name: '待办工单',
          data: convert(res[0]),
        },
        { key: WORK_ORDER_TABS[1], name: '已办工单', data: convert(res[1]) },
        { key: WORK_ORDER_TABS[2], name: '我发起的', data: convert(res[2]) },
        { key: 'FOLLOW', name: '我关注的', data: convert(res[3]) },
        { key: WORK_ORDER_TABS[3], name: '已取消的', data: convert(res[4]) },
      ]
      get().setData({ workOrderCollect })
    })
  },
  setKeywords: keyword => {
    const { setData } = get()
    setData({ keyword })
  },
  fetchWorkOrderData: async (tab, appCode, extra: any = {}) => {
    const { setData, data } = get()
    let fn = tab === 'FOLLOW' ? getFollowList : getTodoItem
    return fn({
      tab,
      appCode,
      pageSize: PAGE_SIZE,
      keyword: data.keyword,
      ...extra,
      // minId: workOrderAppSelected?.id,
    }).then((res: any) => {
      if (_selectedItemKey !== tab + (appCode || '')) return
      let workOrderData = Array.from(res).map((d: any) => {
        // 前端 - 临时状态
        const midItem = data.workOrderMidState[d.procInstId]
        // console.log('midItem', midItem)
        if (midItem && midItem.state === d.processInstance.state) {
          return {
            ...d,
            state: midItem.state,
            stateText: midItem.stateText,
            stateColor: midItem.stateColor,
            uid: uniqueId('order' + d.id),
          }
        }
        return { ...d, uid: uniqueId('order' + d.id) }
      })
      if (extra.maxId) {
        workOrderData = [...workOrderData, ...get().data.workOrderData]
      } else if (extra.minId) {
        workOrderData = [...get().data.workOrderData, ...workOrderData]
      }
      workOrderData = workOrderData.map(d => {
        d.contentList = workOrder.contentExtract({ title: d.title, content: d.content })
        return d
      })
      setData({
        workOrderData,
        loadingDict: { ...data.loadingDict, [_selectedItemKey]: 0 },
        noMoreDict: { ...data.noMoreDict, [_selectedItemKey]: res.length < PAGE_SIZE },
      })
      _selectedItemKey = ''
      return res
    })
  },
  fetchWorkOrderPrev: async () => {
    const { setData, data, fetchWorkOrderData } = get()
    const item = data.workOrderData[0]
    if (data.loadingDict[_selectedItemKey] || !item)
      return Promise.reject(`loading ${_selectedItemKey}`)
    _selectedItemKey = data.selectedTabKey + (data.selectedAppCode || '')
    setData({ loadingDict: { ...data.loadingDict, [_selectedItemKey]: 2 } })
    return fetchWorkOrderData(data.selectedTabKey, data.selectedAppCode, { maxId: item.id })
  },
  fetchWorkOrderNext: async () => {
    const { data, setData, fetchWorkOrderData } = get()
    const item = data.workOrderData[data.workOrderData.length - 1]
    if (data.loadingDict[_selectedItemKey] || !item) {
      return Promise.reject(`loading ${_selectedItemKey}`)
    }
    _selectedItemKey = data.selectedTabKey + (data.selectedAppCode || '')
    setData({ loadingDict: { ...data.loadingDict, [_selectedItemKey]: 1 } })
    return fetchWorkOrderData(data.selectedTabKey, data.selectedAppCode, { minId: item.id })
  },
  selectWorkOrderApp: async (selectedTabKey, appItem) => {
    const { setData, setKeywords, fetchWorkOrderData, data } = get()
    // console.log('workOrderAppSelected', workOrderAppSelected)
    _selectedItemKey = selectedTabKey + (appItem?.appCode || '')
    if (data.selectedTabKey + data.selectedAppCode === _selectedItemKey) return Promise.reject()
    setData({
      selectedTabKey,
      selectedAppCode: appItem?.appCode,
      loadingDict: { ...data.loadingDict, [_selectedItemKey]: 1 },
    })
    setKeywords(void 0)
    return fetchWorkOrderData(selectedTabKey, appItem?.appCode, { keyword: void 0 })
  },

  reloadWorkOrderData: async (keyword, opt = { loading: true } as any) => {
    const { data, setData, fetchWorkOrderData, fetchWorkOrderCollect } = get()
    fetchWorkOrderCollect()
    _selectedItemKey = data.selectedTabKey + (data.selectedAppCode || '')
    if (opt.loading) {
      setData({ loadingDict: { ...data.loadingDict, [_selectedItemKey]: 3 } })
    }

    return fetchWorkOrderData(data.selectedTabKey, data.selectedAppCode, {
      keyword: keyword || data.keyword,
    })
  },

  pollReload: async (keyword, opt) => {
    const { setData, reloadWorkOrderData, pollReload, data } = get()
    const res = await reloadWorkOrderData(keyword, opt)

    const midList: any[] = Object.values(data.workOrderMidState).filter(d => !!d)

    midList.forEach(item => {
      const record = res?.find(d => d.procInstId === item.procInstId)
      if (record && record.processInstance.state !== item.state) {
        data.workOrderMidState[item.procInstId] = null
      }
    })

    setData({ workOrderMidState: data.workOrderMidState })
    if (pollCount > 0 && midList.length) {
      pollTimer = setTimeout(() => {
        pollCount--
        if (pollCount <= 0) {
          data.workOrderMidState = {}
          setData({ workOrderMidState: data.workOrderMidState })
        }

        pollReload(keyword, { loading: false })
      }, 10 * 1000)
    }
  },

  reloadWorkOrderDataAsync: async reloadOpt => {
    const { data, setData, pollReload } = get()
    const stateAttr = {
      state: reloadOpt.state || 'ACTIVE',
      stateText: reloadOpt.stateText || '执行中',
      stateColor: '#FAAD14',
    }
    data.workOrderMidState[reloadOpt.procInstId] = {
      ...reloadOpt,
      ...stateAttr,
    } // { id, state, midState }

    const newData = data.workOrderData.map(d =>
      d.procInstId === reloadOpt.procInstId
        ? {
            ...d,
            ...stateAttr,
          }
        : d
    )

    setData({ workOrderMidState: data.workOrderMidState, workOrderData: newData })
    clearTimeout(pollTimer)
    pollCount = 5
    pollReload(reloadOpt.keyword)
  },

  selectItem: async item => {
    const { setData } = get()
    setData({ selectedItemId: item.id })
  },
  selectItemByParams: async (selectedTabKey, selectedAppCode, selectedItemId) => {
    const { setData, fetchWorkOrderData, data } = get()
    _selectedItemKey = selectedTabKey + (selectedAppCode || '')
    setData({ loadingDict: { ...data.loadingDict, [_selectedItemKey]: 1 } })

    fetchWorkOrderData(selectedTabKey, selectedAppCode, { idLe: selectedItemId })

    setData({ selectedTabKey, selectedAppCode, selectedItemId })
  },
  setWorkOrderPath: workOrderPath => get().setData({ workOrderPath }),

  routeOfPath: (path, { search, replace, navigate, appName = 'flow' }) => {
    const { setWorkOrderPath } = get()

    const activeApps = getActiveApps({ excludeHiddenApp: true })
    // console.log('activeApps', activeApps)
    // navigate(fullPath, { replace: true })
    // const fullPath = '/' + appName + path.replace('/' + appName, '') + search
    const fullPath = '/' + appName + path.replace('/' + appName, '') + search
    setWorkOrderPath(fullPath)

    setTimeout(() => {
      // console.log('activeApps.includes(appName)', activeApps.includes(appName))
      if (activeApps.includes(appName)) {
        microApp.router.push({
          name: appName,
          path: fullPath,
          replace,
        })
      } else {
        // navigate(fullPath, { replace })
        navigatePage('/' + appName, path, navigate, '', search)
        setTimeout(() => {
          microApp.router.push({
            name: appName,
            path: fullPath,
            replace: true,
          })
        })
      }
    }, 20)
    return fullPath
  },

  routeNextItem: navigate => {
    const { routeWorkOrderItem, data, selectItem, fetchWorkOrderData } = get()
    const item = data.workOrderData.find(d => d.id !== data.selectedItemId)

    if (item) {
      selectItem(item)
      routeWorkOrderItem(item, navigate)
      fetchWorkOrderData(data.selectedTabKey, data.selectedAppCode)
    } else {
      navigate('/flow/empty')
      // 重新渲染子应用my-app，并彻底删除缓存值
      microApp.reload('flow', true).then(result => {
        if (result) {
          console.log('重新渲染成功')
        } else {
          console.log('重新渲染失败')
        }
      })
    }
  },

  formatWorkOrderSearch: ({
    taskId,
    procInstId,
    tab,
    app,
    item,
    elecId,
    refundApplyTempId,
    workOrderNo,
    state,
    currentTaskId,
  }: any = {}) => {
    return `?taskId=${taskId || ''}&procInstId=${procInstId || ''}&tab=${tab}&app=${
      app || ''
    }&item=${item}&elecId=${elecId || ''}&refundApplyTempId=${refundApplyTempId || ''}&workOrderNo=${workOrderNo || ''}&state=${state || ''}&currentTaskId=${currentTaskId || ''}`
  },

  routeWorkOrderItem: (
    orderItem,
    navigate,
    { selectedTabKey, selectedAppCode, replace } = {
      selectedTabKey: '',
      selectedAppCode: '',
      replace: false,
    }
  ) => {
    const { data, routeOfPath, formatWorkOrderSearch } = get()
    const {
      flowTaskId,
      procInstId,
      workOrderNo,
      appCode,
      procDefKey,
      id,
      bizData,
      processInstance,
      currentTaskId,
    } = orderItem
    const processState = processInstance?.state

    // get().setData({
    //   selectedAppCode: appCode,
    // })

    const appName = 'flow'
    const tabKey = selectedTabKey || data.selectedTabKey
    const selectedApp = selectedAppCode || appCode || data.selectedAppCode

    const search = formatWorkOrderSearch({
      procInstId,
      taskId: flowTaskId,
      tab: tabKey,
      app: selectedApp,
      item: id,
      elecId: bizData?.elecId,
      refundApplyTempId: bizData?.refundApplyTempId,
      workOrderNo,
      state: processState,
      currentTaskId,
    })

    const path = `/${procDefKey || ''}`

    const fullPath = routeOfPath(path, { search, replace, navigate, appName })

    return { appName, fullPath, search, path }
  },

  routeBizWorkOrderItem: (record, navigate,) => {
    const { routeOfPath } = get()
    const path = workOrderPath[record.workOrder?.bizType];
    let search = '';
    if(record?.workOrder?.bizType==='PRE_APPOINT_ROOM'||record?.workOrder?.bizType==='EDIT_ROOM_PRE_INFO'){
      search = `?workOrderId=${record?.workOrder?.id}&workOrderNo=${record?.workOrder?.workOrderNo}&measureRoomId=${record?.bizData?.measureRoom?.id}`;
    }
    routeOfPath(path, { appName: 'flow', navigate, search })
  },
}))

export default useWorkOrderStore
